import { FC, ReactNode } from 'react';

import ChevronDown from '@/assets/svg/ui-elements/chevron-down.svg';

import { theme } from '../../theme';
import Typography from '../Typography';
import { ExpandRegion } from './expand-region.styled';
import {
  CardDetails,
  CardDivider,
  CardIdentity,
  CardRoot,
  CardSubtitle,
  CardTitle,
  CardTitleAction,
  CardTitleBox,
  CardTitleIcon,
  CardTitleLink,
  CardTitleRow,
  CardToggle,
  FieldRoot,
  FieldValue,
} from './mobile-table-card.styled';

export { FieldsGrid as MobileFieldsGrid } from './mobile-table-card.styled';

type MobileTableCardProps = {
  title: string;
  subtitle?: string;
  startIcon?: ReactNode;
  titleTo?: string;
  onTitleClick?: () => void;
  badge?: ReactNode;
  open: boolean;
  onToggle: () => void;
  toggleLabel?: string;
  children: ReactNode;
};

export const MobileTableCard: FC<MobileTableCardProps> = ({
  title,
  subtitle,
  startIcon,
  titleTo,
  onTitleClick,
  badge,
  open,
  onToggle,
  toggleLabel = 'Toggle activity',
  children,
}) => {
  const titleText = (
    <>
      <CardTitle variant="body2" fontWeight={500}>
        {title}
      </CardTitle>
      {subtitle && <CardSubtitle variant="body3">{subtitle}</CardSubtitle>}
    </>
  );

  return (
    <CardRoot>
      <CardIdentity>
        <CardTitleRow>
          {startIcon && <CardTitleIcon>{startIcon}</CardTitleIcon>}

          {titleTo ? (
            <CardTitleLink to={titleTo}>{titleText}</CardTitleLink>
          ) : onTitleClick ? (
            <CardTitleAction onClick={onTitleClick}>{titleText}</CardTitleAction>
          ) : (
            <CardTitleBox>{titleText}</CardTitleBox>
          )}

          <CardToggle onClick={onToggle} aria-expanded={open} aria-label={toggleLabel}>
            <ChevronDown style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }} />
          </CardToggle>
        </CardTitleRow>

        {badge}
      </CardIdentity>

      <ExpandRegion $open={open}>
        <div>
          <CardDetails>
            <CardDivider />
            {children}
          </CardDetails>
        </div>
      </ExpandRegion>
    </CardRoot>
  );
};

type MobileCardFieldProps = {
  label: string;
  value?: ReactNode;
  fullWidth?: boolean;
  children?: ReactNode;
};

export const MobileCardField: FC<MobileCardFieldProps> = ({
  label,
  value,
  fullWidth,
  children,
}) => (
  <FieldRoot $fullWidth={fullWidth}>
    <Typography variant="body4" color={theme.colors.text.secondary}>
      {label}
    </Typography>
    {children ?? (
      <FieldValue variant="body3" fontWeight={500}>
        {value}
      </FieldValue>
    )}
  </FieldRoot>
);
